import { ExternalLink } from "lucide-react";

/**
 * A whole-teddy-bear-shaped Amazon buy button, matching the cub on the
 * "The World Needs Your Light" book cover. The bear IS the button.
 *
 * Composition:
 *  - Little crescent moon + sparkle stars in the corner (nod to the cover)
 *  - Rounded ears with rosy inner pink
 *  - Fluffy head with muzzle, nose, closed happy eyes
 *  - Round golden belly (the label "Amazon" is baked in)
 *  - Two curled front paws hugging the belly
 *  - Two little back feet peeking out at the bottom
 *  - Gentle bob + tilt on hover
 */
export default function BearButton({ href, label = "Amazon" }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Buy on ${label}`}
      className="bear-btn"
      data-testid="book-buy-amazon"
    >
      <svg
        viewBox="0 0 220 200"
        width="200"
        height="182"
        role="img"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="bearFur" cx="45%" cy="35%" r="70%">
            <stop offset="0%" stopColor="#E9B87A" />
            <stop offset="55%" stopColor="#BF864A" />
            <stop offset="100%" stopColor="#7E4E23" />
          </radialGradient>
          <radialGradient id="bearBelly" cx="50%" cy="45%" r="65%">
            <stop offset="0%" stopColor="#F7E1B6" />
            <stop offset="100%" stopColor="#D9AC72" />
          </radialGradient>
          <radialGradient id="bearMoon" cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor="#FFF4C2" />
            <stop offset="100%" stopColor="#F1C64A" />
          </radialGradient>
          <filter id="bearSoftShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="5" stdDeviation="5" floodColor="#5C3617" floodOpacity="0.32" />
          </filter>
          <filter id="moonGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" />
          </filter>
        </defs>

        {/* Twinkles */}
        <g fill="#FFF4C2">
          <circle cx="200" cy="24" r="1.8" opacity="0.9" />
          <circle cx="188" cy="52" r="1.2" opacity="0.7" />
          <circle cx="18" cy="76" r="1.4" opacity="0.75" />
          <circle cx="10" cy="120" r="1.1" opacity="0.6" />
          <circle cx="210" cy="112" r="1.3" opacity="0.7" />
        </g>

        {/* Little crescent moon behind bear (top-left, echoes the cover) */}
        <g>
          <circle cx="34" cy="30" r="14" fill="url(#bearMoon)" filter="url(#moonGlow)" opacity="0.55" />
          <circle cx="34" cy="30" r="11" fill="url(#bearMoon)" />
          <circle cx="40" cy="26" r="9" fill="#0E1A38" />
        </g>

        {/* LEFT EAR */}
        <g filter="url(#bearSoftShadow)">
          <circle cx="72" cy="46" r="17" fill="url(#bearFur)" />
          <circle cx="72" cy="48" r="8.5" fill="#F0B4B0" />
        </g>

        {/* RIGHT EAR */}
        <g filter="url(#bearSoftShadow)">
          <circle cx="148" cy="46" r="17" fill="url(#bearFur)" />
          <circle cx="148" cy="48" r="8.5" fill="#F0B4B0" />
        </g>

        {/* HEAD */}
        <g filter="url(#bearSoftShadow)">
          <ellipse cx="110" cy="78" rx="50" ry="46" fill="url(#bearFur)" />
        </g>

        {/* Fluffy fur tufts along head silhouette */}
        <g fill="#8E5A2C" opacity="0.55">
          <circle cx="66" cy="72" r="4" />
          <circle cx="62" cy="88" r="3" />
          <circle cx="158" cy="72" r="4" />
          <circle cx="162" cy="88" r="3" />
          <circle cx="90" cy="40" r="3" />
          <circle cx="130" cy="40" r="3" />
        </g>

        {/* Muzzle */}
        <ellipse cx="110" cy="92" rx="24" ry="17" fill="url(#bearBelly)" />

        {/* Nose */}
        <ellipse cx="110" cy="82" rx="6" ry="4.5" fill="#3D2818" />
        <ellipse cx="108" cy="80.5" rx="1.5" ry="1" fill="#FFF" opacity="0.7" />

        {/* Smiley mouth (looking-up happy) */}
        <path d="M 110 87 L 110 92" stroke="#3D2818" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M 110 92 Q 104 98 100 94" stroke="#3D2818" strokeWidth="1.8" fill="none" strokeLinecap="round" />
        <path d="M 110 92 Q 116 98 120 94" stroke="#3D2818" strokeWidth="1.8" fill="none" strokeLinecap="round" />

        {/* Eyes — happy closed arcs (looking up like the cover) */}
        <path d="M 86 68 Q 92 63 98 68" stroke="#3D2818" strokeWidth="2.2" fill="none" strokeLinecap="round" />
        <path d="M 122 68 Q 128 63 134 68" stroke="#3D2818" strokeWidth="2.2" fill="none" strokeLinecap="round" />

        {/* Rosy cheeks */}
        <ellipse cx="78" cy="94" rx="6" ry="4" fill="#F0B4B0" opacity="0.75" />
        <ellipse cx="142" cy="94" rx="6" ry="4" fill="#F0B4B0" opacity="0.75" />

        {/* BODY */}
        <g filter="url(#bearSoftShadow)">
          <ellipse cx="110" cy="150" rx="66" ry="42" fill="url(#bearFur)" />
        </g>

        {/* Belly patch — where the label lives */}
        <ellipse cx="110" cy="152" rx="40" ry="28" fill="url(#bearBelly)" />

        {/* LEFT arm/paw (curled toward belly) */}
        <g filter="url(#bearSoftShadow)">
          <ellipse cx="58" cy="146" rx="16" ry="20" fill="url(#bearFur)" transform="rotate(-18 58 146)" />
        </g>
        <ellipse cx="58" cy="156" rx="9" ry="7" fill="#F0B4B0" opacity="0.8" transform="rotate(-18 58 156)" />

        {/* RIGHT arm/paw */}
        <g filter="url(#bearSoftShadow)">
          <ellipse cx="162" cy="146" rx="16" ry="20" fill="url(#bearFur)" transform="rotate(18 162 146)" />
        </g>
        <ellipse cx="162" cy="156" rx="9" ry="7" fill="#F0B4B0" opacity="0.8" transform="rotate(18 162 156)" />

        {/* Back feet peeking out */}
        <ellipse cx="82" cy="190" rx="18" ry="8" fill="url(#bearFur)" />
        <ellipse cx="82" cy="192" rx="9" ry="4" fill="#F0B4B0" opacity="0.8" />
        <ellipse cx="138" cy="190" rx="18" ry="8" fill="url(#bearFur)" />
        <ellipse cx="138" cy="192" rx="9" ry="4" fill="#F0B4B0" opacity="0.8" />

        {/* Tiny toe beans */}
        <g fill="#8E5A2C" opacity="0.55">
          <circle cx="76" cy="190" r="1.1" />
          <circle cx="82" cy="192" r="1.1" />
          <circle cx="88" cy="190" r="1.1" />
          <circle cx="132" cy="190" r="1.1" />
          <circle cx="138" cy="192" r="1.1" />
          <circle cx="144" cy="190" r="1.1" />
        </g>

        {/* Label — hugged into the belly */}
        <text
          x="110"
          y="156"
          textAnchor="middle"
          fontFamily="'Fredoka', sans-serif"
          fontSize="22"
          fontWeight="700"
          fill="#5C3617"
          style={{ paintOrder: "stroke", stroke: "#F7E1B6", strokeWidth: 0.6 }}
        >
          {label}
        </text>
        <text
          x="110"
          y="172"
          textAnchor="middle"
          fontFamily="'Fredoka', sans-serif"
          fontSize="10"
          fontWeight="600"
          fill="#5C3617"
          opacity="0.9"
        >
          grab yours &rarr;
        </text>
      </svg>
      <span className="bear-btn-external" aria-hidden="true">
        <ExternalLink size={12} />
      </span>
    </a>
  );
}
